<template>
  <div :class="['content', programStyle]">
    <div v-if="!showSILetter && !showAppealDetails">
      <div class="upper">
        <div class="title">ACTIVITY SUMMARY TIMELINE</div>
      </div>
      <ActivitySummary :columnData="activitySummary"></ActivitySummary>
      <div class="upper">
        <div class="title">LETTER DETAILS</div>
      </div>
      <div class="letter-details-table flex-row">
        <DataTable
          :headings="lettersTableTH"
          :rows="letterDetails"
          :perPage="rowCount"
          :page="currentPage"
          :sortedKeyName="'letterDate'"
          :tableName="'letter-details'"
        >
          <template v-slot:infractionNameSlot="slotProps">
            <a v-if="slotProps.row.infractionName !== 'Removal'" @click="viewSILetter(slotProps.row)">{{
              slotProps.row.infractionName
            }}</a>
            <a
              v-else-if="
                slotProps.row.infractionName === 'Removal' &&
                slotProps.row.removalPdfMeta.length > 0 &&
                slotProps.row.removalPdfMeta[0] !== null &&
                slotProps.row.removalPdfMeta[0].archiveUrl
              "
              :href="slotProps.row.removalPdfMeta[0].archiveUrl"
              >{{ slotProps.row.infractionName }}</a
            >
            <a v-else class="letter-not-found" @click="showErrorMessage">
              {{ slotProps.row.infractionName }}
            </a>
          </template>
          <template v-slot:stageSlot="slotProps">
            <a v-if="slotProps.row.stage !== '-'" @click="viewAppeal(slotProps.row)">{{ slotProps.row.stage }}</a>
            <span v-else>-</span>
          </template>
          <template v-slot:acknowledgementDateSlot="slotProps">
            <span
              v-if="
                slotProps.row.acknowledgementDate === 'N/A' ||
                slotProps.row.acknowledgementDate === '-' ||
                slotProps.row.acknowledgementDate === null
              "
              >N/A</span
            >
          </template>
        </DataTable>
      </div>
      <div class="row-container">
        <div class="row-info">
          {{ $t('viewing-firstrow-lastrow-of-forms-totalcount', [firstRow, lastRow, totalCount]) }}
        </div>
        <GPagination
          v-if="pageCount > 1"
          class="pagination"
          v-model="page"
          :pageCount="pageCount"
          @changed="getLetterDetails"
        />
      </div>
    </div>
    <div v-if="showSILetter && !showAppealDetails">
      <router-view :appealType="letterType" :entity="entity" @exit="clearForm" />
    </div>
    <div v-if="!showSILetter && showAppealDetails">
      <router-view :entity="entity" @exit="clearForm" />
    </div>
  </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { mapState } from 'vuex';
import DataTable from '@/components/DataTable.vue';
import activitySummaryTH from '@/utils/tableHeaders/activitySummaryTH';
import lettersTableTH from '@/utils/tableHeaders/surveyinterference/lettersTableTH';
import appealsTableTH from '@/utils/tableHeaders/surveyinterference/appealsTableTH';
import GPagination from '@/components/GComponents/GPagination.vue';
import ActivitySummary from '@/components/SurveyInterference/ActivitySummary.vue';

export default {
  name: 'DealerSI',
  components: { DataTable, GPagination, ActivitySummary },
  props: {
    navOrigin: {
      type: String,
    },
    entity: {
      type: String,
    },
  },
  data() {
    return {
      rowCount: 999,
      currentPage: 1,
      activitySummaryTH: activitySummaryTH(),
      lettersTableTH: lettersTableTH(),
      appealsTableTH: appealsTableTH(),
      page: 1,
      totalCount: null,
      rowCountDropValue: { label: this.$t('show-10'), value: 10 },
      activitySummary: [],
      letterDetails: [],
      letterType: null,
      showSILetter: false,
      showAppealDetails: false,
      isAppealDetailsLocked: false,
      submittedForms: [],
    };
  },
  methods: {
    clearForm() {
      this.showSILetter = false;
      this.showAppealDetails = false;
      this.currentPage = 1;
      this.letterType = null;
      this.getActivitySummary();
      this.getLetterDetails();
    },
    getSubmittedForms() {
      const loader = this.$loading.show();

      const { entity } = this;

      // Survey Interference needs to be checked across all domains.
      const domain = 'ALL';
      const type = 'Survey-Interference';

      this.$store
        .dispatch('getSubmittedForms', { domain, entity, type })
        .then((res) => {
          this.submittedForms = res;
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    viewSILetter(selectedLetter) {
      this.letterType = selectedLetter.letterType;

      this.submittedForms.forEach((letter) => {
        if (letter.formId === selectedLetter.formId) {
          this.$store.state.otherAppealInfo = letter;
          this.showSILetter = true;
        }
      });

      if (this.showSILetter) {
        if (this.$route.name === 'Summary') {
          this.$router.push({
            name: 'Survey-Interference-Letter-Field',
          });
        } else {
          this.$router.push({
            name: 'Survey-Interference-Letter',
          });
        }
      } else {
        this.$notify({
          group: 'primary',
          type: 'error',
          text: "The selected letter doesn't have a corresponding form.",
        });
      }
    },
    viewAppeal(appeal) {
      if (!this.isAppealDetailsLocked) {
        this.showAppealDetails = true;
        if (this.$route.name === 'Summary') {
          this.$router.push({
            name: 'Appeal-Details-Field',
            params: {
              period: appeal.period,
              letterDate: appeal.letterDate,
              letterType: appeal.letterType,
              market: appeal.market,
            },
          });
        } else {
          this.$router.push({
            name: 'Appeal-Details',
            params: {
              period: appeal.period,
              letterDate: appeal.letterDate,
              letterType: appeal.letterType,
              market: appeal.market,
            },
          });
        }
      } else {
        this.$notify({
          group: 'primary',
          type: 'error',
          text: 'Please ensure all Survey Interference letters have been acknowledged before appealing.',
        });
      }
    },
    getActivitySummary() {
      const loader = this.$loading.show();

      const { entity } = this;

      this.$store
        .dispatch('getActivitySummary', { entity })
        .then((res) => {
          this.activitySummary = res.resultObj.allMonthsSIData;
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    getLetterDetails() {
      const loader = this.$loading.show();

      const { entity } = this;
      const page = this.page - 1;

      this.$store
        .dispatch('getLetterDetails', { entity, page })
        .then((res) => {
          this.letterDetails = res.resultObj;
          this.totalCount = res.totalLetterCount;
          this.isAppealDetailsLocked = res.isSIAppealLocked;
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    showErrorMessage() {
      this.$notify({
        group: 'primary',
        type: 'error',
        text: 'Removal letter not found.',
      });
    },
  },
  computed: {
    ...mapState('global', ['programStyle', 'programName']),
    ...mapState(['activeFilters', 'activeNavBar', 'activeNavTab']),
    domain() {
      return this.programName;
    },
    pageCount() {
      if (this.letterDetails) {
        const l = this.totalCount;
        const s = this.rowCountDropValue.value;
        return Math.ceil(l / s);
      }
      return null;
    },
    firstRow() {
      if (this.letterDetails.length > 0) {
        return (this.page - 1) * 10 + 1;
      }
      return 0;
    },
    lastRow() {
      return (this.page - 1) * 10 + this.letterDetails.length;
    },
  },
  watch: {
    programName() {
      this.getActivitySummary();
      this.getLetterDetails();
      this.getSubmittedForms();
    },
  },
  created() {
    this.getActivitySummary();
    this.getLetterDetails();
    this.getSubmittedForms();
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #243882;
  }
}
.upper {
  padding: 37px;
}
.title {
  color: #243882;
  font-size: 18px;
  line-height: 24px;
  text-transform: uppercase;
  font-weight: 600;
}
.activity-summary-table {
  width: 100%;
  justify-content: flex-start;
  .table-left {
    width: 250px;
    list-style-type: none;
    background-color: #56687b;
    margin: 0px;
    padding: 0px;
    color: $primary-white;
    li {
      border-bottom: 1px solid $primary-white;
      height: 24.59px;
      padding-left: 80px;
    }
    li:first-child {
      height: 45px;
    }
  }
  .table-right {
    width: 100%;
  }
}
.letter-details-table {
  width: 100%;
  border-bottom: 2px solid #f5f7f8;
  .data-table {
    width: 100%;
  }
  a {
    cursor: pointer;
  }
  .letter-not-found {
    color: $primary-black;
  }
  .letter-not-found:hover {
    text-decoration: none;
    cursor: default;
  }
}
</style>
